import type { Shift, Attendance, PayrollEntry } from '../types';

let shiftDatabase: Shift[] = [
  {
    id: 'shift-1',
    staff_id: 'staff-1',
    date: '2024-01-15',
    start_time: '08:00',
    end_time: '16:00',
    shift_type: 'morning',
    status: 'completed',
    notes: 'Regular morning shift',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'shift-2',
    staff_id: 'staff-2',
    date: '2024-01-15',
    start_time: '16:00',
    end_time: '00:00',
    shift_type: 'night',
    status: 'scheduled',
    notes: 'Night shift coverage',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'shift-3',
    staff_id: 'staff-1',
    date: '2024-01-16',
    start_time: '08:00',
    end_time: '16:00',
    shift_type: 'morning',
    status: 'scheduled',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let attendanceDatabase: Attendance[] = [
  {
    id: 'attendance-1',
    staff_id: 'staff-1',
    date: '2024-01-15',
    check_in_time: '08:00',
    check_out_time: '16:00',
    hours_worked: 8,
    status: 'present',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'attendance-2',
    staff_id: 'staff-2',
    date: '2024-01-15',
    check_in_time: '16:05',
    check_out_time: '00:00',
    hours_worked: 7.92,
    status: 'late',
    notes: '5 minutes late',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let payrollDatabase: PayrollEntry[] = [
  {
    id: 'payroll-1',
    staff_id: 'staff-1',
    month: '2024-01',
    basic_salary: 12500,
    overtime_hours: 8,
    overtime_rate: 25,
    overtime_pay: 200,
    deductions: 500,
    bonus: 1000,
    gross_pay: 13700,
    net_pay: 13200,
    status: 'approved',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'payroll-2',
    staff_id: 'staff-2',
    month: '2024-01',
    basic_salary: 6250,
    overtime_hours: 12,
    overtime_rate: 18.75,
    overtime_pay: 225,
    deductions: 250,
    bonus: 500,
    gross_pay: 6975,
    net_pay: 6725,
    status: 'draft',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let nextShiftId = 4;
let nextAttendanceId = 3;
let nextPayrollId = 3;

export const shiftService = {
  async getAll(): Promise<Shift[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...shiftDatabase];
  },

  async getByDateRange(startDate: string, endDate: string): Promise<Shift[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return shiftDatabase.filter(shift => 
      shift.date >= startDate && shift.date <= endDate
    );
  },

  async getById(id: string): Promise<Shift | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return shiftDatabase.find(shift => shift.id === id) || null;
  },

  async create(shiftData: Omit<Shift, 'id' | 'created_at' | 'updated_at'>): Promise<Shift> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newShift: Shift = {
      ...shiftData,
      id: `shift-${nextShiftId++}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    shiftDatabase.push(newShift);
    return newShift;
  },

  async update(id: string, updates: Partial<Shift>): Promise<Shift> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = shiftDatabase.findIndex(shift => shift.id === id);
    if (index === -1) {
      throw new Error('Shift not found');
    }
    
    const updatedShift = {
      ...shiftDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    shiftDatabase[index] = updatedShift;
    return updatedShift;
  },

  async delete(id: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = shiftDatabase.findIndex(shift => shift.id === id);
    if (index === -1) {
      throw new Error('Shift not found');
    }
    
    shiftDatabase.splice(index, 1);
  }
};

export const attendanceService = {
  async getAll(): Promise<Attendance[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...attendanceDatabase];
  },

  async getByMonth(month: string): Promise<Attendance[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return attendanceDatabase.filter(attendance => 
      attendance.date.startsWith(month)
    );
  },

  async create(attendanceData: Omit<Attendance, 'id' | 'created_at' | 'updated_at'>): Promise<Attendance> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newAttendance: Attendance = {
      ...attendanceData,
      id: `attendance-${nextAttendanceId++}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    attendanceDatabase.push(newAttendance);
    return newAttendance;
  },

  async bulkImport(attendanceRecords: Omit<Attendance, 'id' | 'created_at' | 'updated_at'>[]): Promise<Attendance[]> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const newRecords = attendanceRecords.map(record => ({
      ...record,
      id: `attendance-${nextAttendanceId++}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }));
    
    attendanceDatabase.push(...newRecords);
    return newRecords;
  }
};

export const payrollService = {
  async getAll(): Promise<PayrollEntry[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...payrollDatabase];
  },

  async getByMonth(month: string): Promise<PayrollEntry[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return payrollDatabase.filter(payroll => payroll.month === month);
  },

  async getById(id: string): Promise<PayrollEntry | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return payrollDatabase.find(payroll => payroll.id === id) || null;
  },

  async create(payrollData: Omit<PayrollEntry, 'id' | 'created_at' | 'updated_at'>): Promise<PayrollEntry> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newPayroll: PayrollEntry = {
      ...payrollData,
      id: `payroll-${nextPayrollId++}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    payrollDatabase.push(newPayroll);
    return newPayroll;
  },

  async update(id: string, updates: Partial<PayrollEntry>): Promise<PayrollEntry> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = payrollDatabase.findIndex(payroll => payroll.id === id);
    if (index === -1) {
      throw new Error('Payroll entry not found');
    }
    
    const updatedPayroll = {
      ...payrollDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    payrollDatabase[index] = updatedPayroll;
    return updatedPayroll;
  }
};
